import React, { useEffect, useState, useMemo } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n/I18nContext';
import { toast, ToastContainer } from 'react-toastify';

const UserList = () => {
    const { t } = useI18n();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // Filters
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await api.get('/admin/users');
            setUsers(response.data);
            setLoading(false);
        } catch (err) {
            setError(err.message || t('common.error'));
            setLoading(false);
        }
    };

    const handleRowClick = (user) => {
        navigate(`/admin/users/edit/${user.type}/${user.id}`);
    };

    const toggleActivation = async (e, user) => {
        e.stopPropagation();
        const nextStatus = !user.active;
        const confirmMsg = nextStatus ? t('admin.userList.confirmActivation') : t('admin.userList.confirmDeactivation');
        if (!window.confirm(confirmMsg)) return;

        try {
            await api.put(`/admin/users/${user.type}/${user.id}/activation?active=${nextStatus}`);
            toast.success(t('common.success'));
            fetchUsers();
        } catch (err) {
            toast.error(err.message || t('common.error'));
        }
    };

    const togglePremium = async (e, user) => {
        e.stopPropagation();
        if (user.type !== 'expediteur') return;
        const nextPremium = !user.premium;

        try {
            await api.put(`/admin/users/expediteur/${user.id}/premium?premium=${nextPremium}`);
            toast.success(t('common.success'));
            fetchUsers();
        } catch (err) {
            toast.error(err.message || t('common.error'));
        }
    };

    const filteredUsers = useMemo(() => {
        return users.filter(user => {
            const matchesSearch =
                (user.email && user.email.toLowerCase().includes(search.toLowerCase())) ||
                (user.enterpriseName && user.enterpriseName.toLowerCase().includes(search.toLowerCase())) ||
                (user.firstname && user.firstname.toLowerCase().includes(search.toLowerCase())) ||
                (user.lastname && user.lastname.toLowerCase().includes(search.toLowerCase()));

            const matchesType = typeFilter === 'all' || user.type === typeFilter;

            return matchesSearch && matchesType;
        });
    }, [users, search, typeFilter]);

    if (loading) {
        return (
            <DashboardLayout>
                <div className="d-flex justify-content-center p-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">{t('common.loading')}</span>
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <ToastContainer />
            <div className="container-fluid p-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h1 className="h3 mb-1 fw-bold text-dark">{t('admin.userList.title')}</h1>
                        <p className="text-muted small mb-0">{t('admin.userList.subtitle')}</p>
                    </div>
                    <button className="btn btn-primary" onClick={() => navigate('/admin/users/create')}>
                        <i className="bi bi-person-plus me-2"></i>{t('admin.createUserAccount')}
                    </button>
                </div>

                <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '0.9375rem' }}>
                    <div className="card-body p-3">
                        <div className="row g-3">
                            <div className="col-md-8">
                                <div className="input-group">
                                    <span className="input-group-text bg-white border-end-0">
                                        <i className="bi bi-search text-muted"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder={t('admin.userList.searchPlaceholder')}
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <select
                                    className="form-select"
                                    value={typeFilter}
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                >
                                    <option value="all">{t('admin.userList.allTypes')}</option>
                                    <option value="transporteur">{t('register.roles.transporteur')}</option>
                                    <option value="expediteur">{t('register.roles.expediteur')}</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card border-0 shadow-sm" style={{ borderRadius: '0.9375rem', overflow: 'hidden' }}>
                    <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="px-4 py-3">{t('admin.userList.table.enterprise')}</th>
                                    <th className="py-3">{t('admin.userList.table.email')}</th>
                                    <th className="py-3">{t('admin.userList.table.firstname')}</th>
                                    <th className="py-3">{t('admin.userList.table.lastname')}</th>
                                    <th className="py-3">{t('admin.userList.table.country')}</th>
                                    <th className="py-3">{t('admin.userList.table.status')}</th>
                                    <th className="py-3 text-center">{t('admin.userList.table.premium')}</th>
                                    <th className="py-3">{t('admin.userList.table.type')}</th>
                                    <th className="px-4 py-3 text-end">{t('admin.userList.table.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((user) => (
                                    <tr key={`${user.type}-${user.id}`} onClick={() => handleRowClick(user)} style={{ cursor: 'pointer' }}>
                                        <td className="px-4 py-3">
                                            <div className="fw-bold text-dark">{user.enterpriseName}</div>
                                        </td>
                                        <td className="py-3 text-muted">{user.email}</td>
                                        <td className="py-3">{user.firstname}</td>
                                        <td className="py-3">{user.lastname}</td>
                                        <td className="py-3">{user.country || '-'}</td>
                                        <td className="py-3">
                                            <span className={`badge rounded-pill ${user.active ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                                                {user.active ? t('admin.userList.status.active') : t('admin.userList.status.inactive')}
                                            </span>
                                        </td>
                                        <td className="py-3 text-center">
                                            {user.type === 'expediteur' ? (
                                                <i className={`bi ${user.premium ? 'bi-star-fill text-warning' : 'bi-star text-muted'}`} style={{ fontSize: '1.1rem' }}></i>
                                            ) : (
                                                user.responsable ? <i className="bi bi-shield-check text-primary" title="Responsable"></i> : '-'
                                            )}
                                        </td>
                                        <td className="py-3">
                                            <span className={`badge bg-light text-dark border`}>
                                                {user.type === 'transporteur' ? t('register.roles.transporteur') : t('register.roles.expediteur')}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-end">
                                            <div className="d-flex justify-content-end gap-2" onClick={e => e.stopPropagation()}>
                                                <button
                                                    className={`btn btn-sm ${user.active ? 'btn-outline-danger' : 'btn-outline-success'}`}
                                                    onClick={(e) => toggleActivation(e, user)}
                                                    title={user.active ? t('admin.userList.actions.deactivate') : t('admin.userList.actions.activate')}
                                                >
                                                    <i className={`bi ${user.active ? 'bi-person-x' : 'bi-person-check'}`}></i>
                                                </button>
                                                {user.type === 'expediteur' && (
                                                    <button
                                                        className={`btn btn-sm ${user.premium ? 'btn-outline-warning' : 'btn-outline-secondary'}`}
                                                        onClick={(e) => togglePremium(e, user)}
                                                        title={user.premium ? t('admin.userList.actions.premiumOff') : t('admin.userList.actions.premiumOn')}
                                                    >
                                                        <i className="bi bi-star"></i>
                                                    </button>
                                                )}
                                                <button className="btn btn-sm btn-outline-primary" onClick={() => handleRowClick(user)}>
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filteredUsers.length === 0 && (
                                    <tr>
                                        <td colSpan="9" className="text-center py-5 text-muted">
                                            {t('missions.missionList.noMissionsFound')}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default UserList;
